import { useEffect, useState } from "react";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFingerprint, faFaceSmile, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { ClientService } from "../../component/services/client.service";
import { makeParams } from "../../component/api/makeRequest";
import { DateRange } from "../../component/DateRangeSelector/DateRangeSelector";
import { useTheme } from "../../theme/useTheme";
import DonutChart, { ChartDataItem } from "../../component/Charts/DonutChart";
import VendorFailureStudentsModal from "./VendorFailureStudentsModal";
import moment from "moment";

interface FailureTypeEntry {
    failureType: string;
    method: string;
    count: number;
}

interface VendorFailureStat {
    vendorName: string;
    totalDistinctFailures: number;
    failureTypes: FailureTypeEntry[];
}

interface Props {
    dateRange: DateRange;
}

const FAILURE_TYPE_COLORS: Record<string, string> = {
    "Facial Failed":      "#3b82f6",
    "Fingerprint Failed": "#f59e0b",
    "Both Failed":        "#ef4444",
    "Other":              "#9ca3af",
};

const FAILURE_TYPE_ICONS: Record<string, any> = {
    "Facial Failed":      faFaceSmile,
    "Fingerprint Failed": faFingerprint,
    "Both Failed":        faTriangleExclamation,
    "Other":              faTriangleExclamation,
};

// Collapse method-level rows into failureType buckets for the donut
function collapseToTypes(failureTypes: FailureTypeEntry[]): Record<string, number> {
    return failureTypes.reduce((acc, f) => {
        acc[f.failureType] = (acc[f.failureType] ?? 0) + f.count;
        return acc;
    }, {} as Record<string, number>);
}

function VendorFailureCard({ vendor, dateRange, onRowClick }: {
    vendor: VendorFailureStat;
    dateRange: DateRange;
    onRowClick: (vendorName: string, failureType: string) => void;
}) {
    const { colors, colorSchemes } = useTheme();

    const byType = collapseToTypes(vendor.failureTypes);
    const chartData: ChartDataItem[] = Object.entries(byType).map(([type, count]) => ({
        category: type,
        value: count,
        fill: FAILURE_TYPE_COLORS[type] ?? "#9ca3af",
    }));

    return (
        <Card
            style={{
                ...colorSchemes.card,
                borderRadius: "14px",
                border: `1px solid ${colors.border.light}`,
                overflow: "hidden",
                height: "100%",
            }}
        >
            {/* Header */}
            <div
                className="d-flex align-items-center gap-3 px-4 py-3"
                style={{ borderBottom: `1px solid ${colors.border.light}`, background: colors.background.secondary }}
            >
                <div
                    style={{
                        width: 36, height: 36, borderRadius: "9px",
                        background: "#fee2e2", display: "flex",
                        alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}
                >
                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#ef4444", fontSize: "0.9rem" }} />
                </div>
                <div>
                    <div style={{ fontWeight: 700, fontSize: "0.95rem", color: colors.text.primary }}>
                        {vendor.vendorName}
                    </div>
                    <div style={{ fontSize: "0.72rem", color: colors.text.muted }}>
                        {vendor.totalDistinctFailures.toLocaleString()} distinct failures
                    </div>
                </div>
            </div>

            <div className="p-3">
                {/* Donut chart */}
                <div className="d-flex justify-content-center">
                    <DonutChart
                        data={chartData}
                        centerText={{ label: "Failures", value: vendor.totalDistinctFailures }}
                        height={220}
                        width={220}
                        showTooltip
                        showLegend={false}
                        showCalloutLabels={false}
                    />
                </div>

                {/* Legend rows */}
                <div className="mt-2">
                    {Object.entries(byType)
                        .sort((a, b) => b[1] - a[1])
                        .map(([type, count]) => {
                            const pct = vendor.totalDistinctFailures
                                ? Math.round((count / vendor.totalDistinctFailures) * 100)
                                : 0;
                            const color = FAILURE_TYPE_COLORS[type] ?? "#9ca3af";
                            return (
                                <div
                                    key={type}
                                    className="d-flex align-items-center justify-content-between py-2"
                                    style={{
                                        borderBottom: `1px solid ${colors.border.light}`,
                                        cursor: "pointer",
                                        borderRadius: "6px",
                                        padding: "8px 6px",
                                        transition: "background 0.15s",
                                    }}
                                    onClick={() => onRowClick(vendor.vendorName, type)}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = colors.background.secondary)}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                                        <FontAwesomeIcon
                                            icon={FAILURE_TYPE_ICONS[type] ?? faTriangleExclamation}
                                            style={{ color, fontSize: "0.75rem" }}
                                        />
                                        <span style={{ fontSize: "0.82rem", color: colors.text.primary, fontWeight: 500 }}>
                                            {type}
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span style={{ fontSize: "0.82rem", fontWeight: 700, color }}>
                                            {count.toLocaleString()}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: "0.7rem", color: colors.text.muted,
                                                background: colors.background.secondary,
                                                borderRadius: "20px", padding: "1px 7px",
                                            }}
                                        >
                                            {pct}%
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>
        </Card>
    );
}

export default function VendorFailureTypes({ dateRange }: Props) {
    const { colors } = useTheme();
    const [data, setData] = useState<VendorFailureStat[]>([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState<{ vendorName: string; failureType: string } | null>(null);

    async function fetchData(start: Date, end: Date) {
        setLoading(true);
        setData([]);
        const params = makeParams([
            { index: "start_time", value: moment(start).toISOString() },
            { index: "end_time", value: moment(end).toISOString() },
        ]);
        await ClientService.getVendorFailureTypes(params)
            .then((res: any) => setData(res.data?.data || []))
            .catch((err: any) => console.log(err))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchData(dateRange.startDate, dateRange.endDate);
    }, [dateRange.startDate, dateRange.endDate]);

    return (
        <div className="mt-4 mb-4">
            <div className="mb-3">
                <h6 style={{ fontWeight: 700, color: colors.text.primary, fontSize: "1rem", marginBottom: 2 }}>
                    Failure Type Breakdown by Vendor
                </h6>
                <span style={{ color: colors.text.muted, fontSize: "0.78rem" }}>
                    Click any failure type to see the student list
                </span>
            </div>

            {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" style={{ color: colors.primary.main }} />
                </div>
            ) : data.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
                    <FontAwesomeIcon
                        icon={faFingerprint}
                        style={{ fontSize: "2.5rem", color: colors.border.secondary, marginBottom: "1rem" }}
                    />
                    <span style={{ color: colors.text.muted, fontSize: "0.9rem" }}>
                        No failure type data for the selected range
                    </span>
                </div>
            ) : (
                <Row className="g-3">
                    {data.map((vendor) => (
                        <Col key={vendor.vendorName} md={6} xl={4}>
                            <VendorFailureCard
                                vendor={vendor}
                                dateRange={dateRange}
                                onRowClick={(vendorName, failureType) => setModal({ vendorName, failureType })}
                            />
                        </Col>
                    ))}
                </Row>
            )}

            {modal && (
                <VendorFailureStudentsModal
                    show={!!modal}
                    onHide={() => setModal(null)}
                    vendorName={modal.vendorName}
                    failureType={modal.failureType}
                    dateRange={dateRange}
                />
            )}
        </div>
    );
}
